import { useEffect, useMemo, useRef, useState } from 'react';

import { Tooltip } from '@mui/material';
import isEqual from 'lodash/isEqual';
import { useSearchParams } from 'react-router-dom';

import {
  Button,
  Icons,
  SortingState,
  useDrawer,
  notify,
} from '@cast/design-system';
import { Notification } from '@cast/types';
import { useDebounce } from '@cast/utils';

import { useAbility } from '@cast/shared-ability-data-access';
import {
  useCompleteSlackWorkspacesInstallMutation,
  useDeleteSlackWorkspaceMutation,
} from 'hooks/mutations/notifications';
import {
  NotificationsFilter,
  useGetNotificationsPaginatedQuery,
  useGetSlackWorkspaces,
} from 'hooks/queries/notifications';
import { PageHeader } from 'main-layout/PageHeader';

import {
  useNavigateToCreateAlert,
  useNavigateToManageAlerts,
} from './_hooks/useNavigateToCreateAlert';
import { AddAlertDrawer, AlertsDrawer } from './alerts-drawer';
import { FormModel } from './alerts-drawer/form';
import { NOTIFICATIONS_FILTERS_STORAGE_KEY } from './constants';
import { notificationsCriteria, NotificationsList } from './list';
import { NotificationsTabs, NotificationsTab } from './NotificationsTabs';
import { CatalogueView } from './catalogue';

import {
  Criterion,
  SearchProvider,
  UseServerDataArgs,
  useSearchContext,
  getInitialValue,
} from '@cast/shared-search-ui';

const pageSize = 50;

const columnSortMap = (columnId?: string) => {
  switch (columnId) {
    case 'clusterName':
      return 'cluster_name';
    case 'operationType':
      return 'operation_type';
    default:
      return columnId || '';
  }
};

const sanitizeStoredValues = (
  stored: unknown,
  criteria: Criterion[],
  initialValues: Record<string, any>
): Record<string, any> | null => {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return null;
  }

  const allowedKeys = new Set(criteria.map(({ key }) => key));
  const sanitized: Record<string, any> = { ...initialValues };

  for (const key of Object.keys(stored)) {
    if (!allowedKeys.has(key)) {
      continue;
    }

    const value = (stored as Record<string, any>)[key];
    if (value == null) {
      delete sanitized[key];
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

const pickPersistedValues = (
  values: Record<string, any> | undefined,
  criteria: Criterion[]
): Record<string, any> => {
  if (!values) {
    return {};
  }

  const allowedKeys = new Set(criteria.map(({ key }) => key));
  return Object.keys(values).reduce<Record<string, any>>((acc, key) => {
    if (!allowedKeys.has(key)) {
      return acc;
    }

    const value = values[key];
    if (value !== undefined) {
      acc[key] = value;
    }

    return acc;
  }, {});
};

const NotificationsFiltersPersistence = () => {
  const searchState = useSearchContext<Notification>();

  const values = searchState?.values;
  const setValues = searchState?.setValues;
  const criteria = useMemo(
    () => searchState?.criteria ?? [],
    [searchState?.criteria]
  );
  const valuesChanged = searchState?.valuesChanged ?? false;

  const initialValues = useMemo(() => getInitialValue(criteria), [criteria]);

  // Load filters from localStorage once on mount
  // Intentionally uses empty deps to avoid overwriting user's active filter selections
  // if SearchProvider re-renders or criteria changes during the session
  useEffect(() => {
    if (!setValues) {
      return;
    }

    const raw = window.localStorage.getItem(NOTIFICATIONS_FILTERS_STORAGE_KEY);

    if (!raw) {
      return;
    }

    try {
      const sanitized = sanitizeStoredValues(
        JSON.parse(raw),
        criteria,
        initialValues
      );

      if (!sanitized) {
        window.localStorage.removeItem(NOTIFICATIONS_FILTERS_STORAGE_KEY);
        return;
      }

      // Only set values if user hasn't modified filters yet
      if (!valuesChanged && !isEqual(values ?? initialValues, sanitized)) {
        setValues(sanitized);
      }
    } catch (error) {
      console.error(
        'Failed to load notifications filters from localStorage:',
        error
      );
      window.localStorage.removeItem(NOTIFICATIONS_FILTERS_STORAGE_KEY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const currentValues = values ?? initialValues;

    if (isEqual(currentValues, initialValues)) {
      window.localStorage.removeItem(NOTIFICATIONS_FILTERS_STORAGE_KEY);
      return;
    }

    const filteredValues = pickPersistedValues(values, criteria);

    if (Object.keys(filteredValues).length === 0) {
      window.localStorage.removeItem(NOTIFICATIONS_FILTERS_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(
      NOTIFICATIONS_FILTERS_STORAGE_KEY,
      JSON.stringify(filteredValues)
    );
  }, [criteria, initialValues, values]);

  return null;
};

const useNotifications = (
  setMeta: (meta: { count?: number; page: number }) => void,
  { values: _values, sortingState, enabled }: UseServerDataArgs
) => {
  const values = useDebounce(_values);
  const filter: NotificationsFilter = {};
  if ('name' in values) {
    filter.name = values.name;
  }
  if ('id' in values) {
    filter.id = values.id;
  }
  if ('clusterName' in values) {
    filter.clusterName = values.clusterName;
  }
  if ('project' in values) {
    filter.project = values.project;
  }
  if ('operation' in values) {
    filter.operationType = values.operation;
  }
  if ('resolved' in values) {
    const resolvedValue = values.resolved;

    if (resolvedValue === 'obsolete') {
      filter.isExpired = true;
    } else if (resolvedValue === 'resolved') {
      filter.isExpired = false;
      filter.onlyUnresolved = false;
    } else if (resolvedValue === 'unresolved') {
      filter.isExpired = false;
      filter.onlyUnresolved = true;
    } else if (resolvedValue === 'all') {
      // no additional filters
    }
  }
  if ('severity' in values) {
    filter.severities = values.severity;
  }
  const hasFiltersApplied = !!Object.keys(filter).length;

  const sorting: SortingState = {
    columnId: columnSortMap(sortingState?.columnId),
    sortBy: {
      accessor: columnSortMap(sortingState?.sortBy?.accessor as string),
      direction: sortingState?.sortBy?.direction || 'asc',
    },
  };

  const res = useGetNotificationsPaginatedQuery({
    filter,
    sortingState: sorting,
    enabled,
    pageSize,
  });
  useEffect(() => {
    if (!res.isLoading) {
      setMeta({ count: res.data?.count, page: res.currentPage });
    }
  }, [res.data?.count, res.currentPage, res.isLoading, setMeta]);

  return {
    data: res.data?.items,
    isLoading: res.isLoading,
    fetchNextPage: res.fetchNextPage,
    fetchPreviousPage: res.fetchPreviousPage,
    hasFiltersApplied,
    error: res.error,
  };
};

export const Notifications = () => {
  const [meta, setMeta] = useState<
    { count?: number; page: number } | undefined
  >();
  const { open } = useDrawer();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasHandledFormStateRef = useRef(false);
  const oauthFormStateRef = useRef<FormModel | null>(null);

  const canCreate = useAbility().can('create', 'OrganizationWebHooks');
  const canEdit = useAbility().can('update', 'OrganizationWebHooks');

  const { data: slackWorkspacesData, isLoading: isLoadingSlackWorkspaces } =
    useGetSlackWorkspaces();

  const { mutate: deleteSlackWorkspace } = useDeleteSlackWorkspaceMutation(
    undefined,
    undefined,
    false
  );

  const { mutateAsync: completeSlackWorkspaceInstall } =
    useCompleteSlackWorkspacesInstallMutation(() => {
      // After completing the installation, open the appropriate drawer
      if (oauthFormStateRef.current) {
        // Coming from OAuth - open AddAlertDrawer with restored form state
        open({
          component: AddAlertDrawer,
          props: { restoredFormState: oauthFormStateRef.current },
        });
        oauthFormStateRef.current = null;
      } else {
        // Direct call - open AlertsDrawer
        open({ component: AlertsDrawer, props: {} });
      }
    });

  // Handle action=create-alert parameter
  useNavigateToCreateAlert(
    () =>
      open({
        component: AddAlertDrawer,
        props: {},
      }),
    canCreate
  );

  // Handle action=manage-alerts parameter
  useNavigateToManageAlerts(
    () =>
      open({
        component: AlertsDrawer,
        props: {},
      }),
    canCreate || canEdit
  );

  // Handle return from Slack OAuth - complete installation and open appropriate drawer
  useEffect(() => {
    if (hasHandledFormStateRef.current) return;

    const stateParam = searchParams.get('state');
    const codeParam = searchParams.get('code');
    const errorParam = searchParams.get('error');
    if (
      stateParam &&
      errorParam &&
      (slackWorkspacesData?.items?.length ?? 0) > 0
    ) {
      slackWorkspacesData?.items?.forEach((w) => deleteSlackWorkspace(w.id!));
    }
    // Check if we're returning from Slack OAuth with state and code
    // Wait for workspaces data to load to avoid race condition
    if (
      stateParam &&
      codeParam &&
      !isLoadingSlackWorkspaces &&
      slackWorkspacesData?.items?.length
    ) {
      try {
        const state = JSON.parse(stateParam);
        if (state.formState && state.mode) {
          hasHandledFormStateRef.current = true;

          // Get the first workspace from the list
          const workspaceId = slackWorkspacesData?.items?.[0]?.id;

          if (!workspaceId) {
            notify.error('No Slack workspace found to complete installation');
            console.error('No Slack workspace found to complete installation');
            return;
          }

          // Store form state for restoration after OAuth
          oauthFormStateRef.current = state.formState;

          // Clean up BOTH params immediately before opening drawer
          // This prevents useSlackOAuthReturn from re-processing stale state
          searchParams.delete('code');
          searchParams.delete('state');
          setSearchParams(searchParams, { replace: true });

          // Complete the Slack workspace installation
          completeSlackWorkspaceInstall({
            id: workspaceId,
            accessCode: codeParam,
            state: state.installState,
          });
        }
      } catch (error) {
        notify.error('Failed to process Slack installation');
        console.error('Failed to parse Slack OAuth state:', error);
      }
    }
  }, [
    searchParams,
    setSearchParams,
    open,
    completeSlackWorkspaceInstall,
    slackWorkspacesData,
    isLoadingSlackWorkspaces,
    deleteSlackWorkspace,
  ]);

  const hasPermissions = canCreate || canEdit;

  const manageAlertsButton = (
    <Button
      testId="alerts-button"
      onClick={() => open({ component: AlertsDrawer, props: {} })}
      startIcon={<Icons.ArrowsLeftRight />}
    >
      Manage alerts
    </Button>
  );

  const [activeTab, setActiveTab] = useState<NotificationsTab>('active');
  const isCatalogueEnabled = useAbility().can(
    'view',
    'FeatureNotificationsCatalogue'
  );

  return (
    <>
      <PageHeader heading="Notifications">
        {hasPermissions ? (
          manageAlertsButton
        ) : (
          <Tooltip title="Owner role required">
            <span>{manageAlertsButton}</span>
          </Tooltip>
        )}
      </PageHeader>
      {isCatalogueEnabled && (
        <NotificationsTabs activeTab={activeTab} onChange={setActiveTab} />
      )}
      {activeTab === 'active' || !isCatalogueEnabled ? (
        <SearchProvider
          criteria={notificationsCriteria}
          urlKey="notifications"
          serverSideSort
          disableFreeText
          useData={useNotifications.bind(null, setMeta)}
        >
          <NotificationsFiltersPersistence />
          <NotificationsList
            count={meta?.count}
            currentPage={meta?.page}
            pageSize={pageSize}
          />
        </SearchProvider>
      ) : (
        <CatalogueView />
      )}
    </>
  );
};
